import { parsePhoneNumber } from "utils/Haim/phoneNumbersUtils";
import { isContactBookBusyError, isWebotServerError } from "./contactWriteErrors";
import type { REST_schema, RealEstateContact } from "../../@types/sharedSchema";

/**
 * Pure helpers for the contact PHONE-CHANGE (re-key) flow.
 *
 * `POST /api/v1/contacts/change-phone` moves a contact — whose Firestore doc id IS its
 * international phone — to a new id, ports every satellite, and hard-deletes the old
 * document. These helpers are the browser-side repairs that follow a successful call:
 * patching the in-memory `allContacts` array, naming the session-cache keys that now
 * describe a document that no longer exists, and rendering the ported-summary line.
 *
 * Deliberately import-light (types, `parsePhoneNumber` and the error classes only) so the
 * test can run without initializing Firebase — which is also why `_metadata` is rebuilt
 * here instead of through `generateMetadataFromContact` (utils/Haim/firestoreUtils pulls in
 * `db`). The shape mirrors that function byte-for-byte; keep the two in sync.
 *
 * `describeChangePhoneFailure` lives here for the same reason: what a failed change MEANS —
 * nothing happened, or half of it did — decides whether the dialog may let the number be
 * edited, and that must be testable without mounting the dialog (whose imports reach
 * Firebase).
 */

/**
 * Replaces the old contact entry with one carrying the new id. Everything else on the
 * document is kept as-is — the server copied the document verbatim, so the in-memory
 * copy stays a faithful mirror; Cloud-Function-derived fields refresh on their own.
 *
 * Returns the SAME array reference when the old id is not present (the CRM page never
 * loads this array), so a no-op never causes a re-render.
 */
export function rekeyContactInList(
  contacts: RealEstateContact[],
  oldContactId: string,
  newContactId: string,
  userEmail: string
): RealEstateContact[] {
  if (!contacts.some((contact) => contact.id === oldContactId)) return contacts;
  return contacts.map((contact) =>
    contact.id === oldContactId
      ? ({
          ...contact,
          id: newContactId,
          callerPhoneNumber: newContactId,
          // Mirrors utils/Haim/firestoreUtils.generateMetadataFromContact — same keys,
          // same values — without importing it (see the module header).
          _metadata: {
            callerPhoneNumber: parsePhoneNumber(newContactId),
            firestorePath: `dashboard-users-v2/${userEmail}/contacts/${newContactId}`
          }
        } as RealEstateContact)
      : contact
  );
}

/** How a failed phone change is shown, and what the dialog lets the user do next. */
export type ChangePhoneFailure = {
  message: string;
  /** The primary action reads "נסה שוב": sending the request again is the right next step. */
  retryable: boolean;
  /**
   * Part of the change may already have happened — the record at the new number, some of
   * its history still under the old one — and only the SAME request finishes it. The dialog
   * then locks the number: changing the target mid-way is exactly what must not happen.
   */
  partial: boolean;
};

const CHANGE_PHONE_FORBIDDEN_MESSAGE =
  "אין לך הרשאה לשנות את המספר של הלקוח הזה. פנו למנהל/ת המשרד.";
const CHANGE_PHONE_BUSY_FALLBACK = "לא הצלחנו לשמור כרגע, נסו שוב בעוד רגע";
const CHANGE_PHONE_PARTIAL_MESSAGE =
  "ההעברה לא הושלמה עד הסוף. אפשר לנסות שוב — ההעברה תמשיך מהנקודה שבה נעצרה.";

/**
 * Maps a failed `POST /contacts/change-phone` onto what the dialog shows.
 *
 * THREE kinds of answer, and telling them apart is the whole point:
 *
 *  - REFUSED, NOTHING MOVED, and sending it again cannot help — 400 (the number itself),
 *    404, 409 (the number is taken; the server's sentence says by what), and 403: in an
 *    office's shared book a number is changed by whoever has the customer on their own list,
 *    or by somebody with full access. These are terminal for THIS input: the user edits the
 *    number, or gives up.
 *  - REFUSED, NOTHING MOVED, try again in a moment — `CRM_BOOK_BUSY`: the office's switch
 *    into (or out of) its shared book is running, or the save could not be routed this
 *    instant. The server's own sentence; "נסה שוב", and the number stays editable because
 *    nothing is mid-way.
 *  - MAYBE HALF DONE — `CRM_REKEY_PARTIAL`, and everything this function cannot name: a
 *    5xx, a dropped connection. Re-sending the SAME request resumes and finishes, so the
 *    number is locked and the action is "נסה שוב".
 *
 * The first version knew only 400 / 404 / 409 and filed everything else under "half done":
 * a member refused by the edit gate was shown "ההעברה לא הושלמה עד הסוף" with a locked
 * number and a retry button that could never succeed — while nothing had moved at all.
 */
export function describeChangePhoneFailure(error: unknown): ChangePhoneFailure {
  if (isWebotServerError(error)) {
    if (error.status === 409) {
      // The message distinguishes a live collision from a soft-deleted twin — render
      // it verbatim; no client-side copy can tell those apart.
      return {
        message: error.serverMessage ?? "קיים כבר לקוח עם המספר החדש — לא ניתן להעביר אליו.",
        retryable: false,
        partial: false
      };
    }
    if (error.status === 400) {
      return {
        message: error.serverMessage ?? "המספר החדש אינו תקין.",
        retryable: false,
        partial: false
      };
    }
    if (error.status === 404) {
      return { message: "הלקוח לא נמצא. ייתכן שנמחק.", retryable: false, partial: false };
    }
    if (error.status === 403 || error.code === "CRM_SCOPE_FORBIDDEN") {
      // The server's own message names e-mails and ids, in English; this is the sentence.
      return { message: CHANGE_PHONE_FORBIDDEN_MESSAGE, retryable: false, partial: false };
    }
    if (isContactBookBusyError(error)) {
      return {
        message: error.serverMessage ?? CHANGE_PHONE_BUSY_FALLBACK,
        retryable: true,
        partial: false
      };
    }
  }
  return { message: CHANGE_PHONE_PARTIAL_MESSAGE, retryable: true, partial: true };
}

/**
 * The customer-page tab ids, as session-cache key prefixes. Kept as a plain list (not
 * imported from the tab registry) because the registry chunk is lazy and this list is
 * read synchronously right after a phone change.
 */
const CONTACT_DETAIL_TAB_CACHE_IDS = [
  "overview",
  "timeline",
  "matches",
  "calls",
  "tasks",
  "meetings",
  "signatures",
  "notes",
  "documents",
  "broadcast"
] as const;

/**
 * Every session-cache key that describes the OLD contact id after a re-key.
 *
 * Two groups:
 *  - `<tabId>:<id>` for each customer-page tab — the convention the tab data modules
 *    follow for their primary key (`calls:<id>`, `notes:<id>`, `signatures:<id>`, …).
 *    Some tabs key differently (tasks/meetings cache per USER, not per contact), so a
 *    few of these keys never exist — invalidating an absent key is a free no-op.
 *  - The known compound keys: the shared get-details dossier (`contact-details:<id>`,
 *    written by both overview and timeline) and the per-tab satellites.
 *
 * Keys carrying non-derivable arguments (per-call transcripts, minisite listing sets)
 * are not enumerable here; they are only ever read via the keys above, which are gone.
 */
export function contactRekeyCacheKeys(contactId: string): string[] {
  return [
    ...CONTACT_DETAIL_TAB_CACHE_IDS.map((tabId) => `${tabId}:${contactId}`),
    `contact-details:${contactId}`,
    `timeline:${contactId}:call-enrichment`,
    `overview:${contactId}:favorites`,
    `overview:${contactId}:latest-ai-call`,
    `matches:${contactId}:favorites`,
    `matches:${contactId}:views`,
    `matches:${contactId}:favorite-listings`,
    `matches:${contactId}:search`,
    `matches:${contactId}:profile-suggestion-dismissed`
  ];
}

/**
 * The ported-summary counters worth showing a realtor, with Hebrew labels, in display
 * order. Internal counters (`listingPageViews`, `whatsappSuppressions`,
 * `listingMatchesDeleted`) are deliberately absent — they describe caches and
 * plumbing, and "נמחקו 3 רשומות התאמה" reads as data loss to someone whose data
 * just moved.
 */
const PORTED_SUMMARY_LABELS: ReadonlyArray<
  [keyof REST_schema.ChangeContactPhonePortedSummary, string]
> = [
  ["userNotes", "הערות"],
  ["calendarEvents", "פגישות ומשימות"],
  ["signatureDocuments", "מסמכי חתימה"],
  ["callSummaries", "שיחות"],
  ["favoriteListings", "נכסים במיניסייט"],
  ["contactDocuments", "מסמכים"],
  ["sentMessages", "הודעות"],
  ["facebookLeads", "לידים מפייסבוק"],
  ["sharedContacts", "שיתופים בסוכנות"]
];

/**
 * "הועברו: 3 הערות · 2 פגישות ומשימות · …" — only the non-zero counters, in the label
 * order above. Empty string when nothing user-visible was ported (the dialog then
 * shows only the "moved to the new number" line). Tolerant of a partial summary
 * object: a missing counter counts as zero.
 */
export function formatPortedSummary(
  summary: Partial<REST_schema.ChangeContactPhonePortedSummary> | null | undefined
): string {
  if (!summary) return "";
  const parts = PORTED_SUMMARY_LABELS.filter(([key]) => {
    const count = summary[key];
    return typeof count === "number" && count > 0;
  }).map(([key, label]) => `${summary[key]} ${label}`);
  return parts.length ? `הועברו: ${parts.join(" · ")}` : "";
}
